// Deterministic serializer for q2q.json (docs/format/README.md §10).
// Pretty envelope, one compact record per line, arrays sorted, keys in schema order.

import type { Anchor, AnyRecord, Doc, Rect, Records, RecordType } from "./types.ts";

/** Round to at most 2 decimals; never -0. */
export function num(v: number): number {
  const r = Math.round(v * 100) / 100;
  return r === 0 ? 0 : r;
}

const str = (s: string): string => s.normalize("NFC");
const rect = (r: Rect): Rect => [num(r[0]), num(r[1]), num(r[2]), num(r[3])];

function anchor(a: Anchor): Anchor {
  return a.shape === "pin"
    ? { page: a.page, shape: "pin", point: [num(a.point[0]), num(a.point[1])] }
    : { page: a.page, shape: "region", rects: a.rects.map(rect) };
}

/** Rebuild a record with its members in schema order and canonical values. */
export const canonical: { [K in RecordType]: (r: Records[K]) => Records[K] } = {
  project: (p) => ({
    title: str(p.title),
    source: { path: str(p.source.path), sha256: p.source.sha256, pageCount: p.source.pageCount },
  }),
  pages: (p) => ({ page: p.page, rotate: p.rotate, view: rect(p.view), label: str(p.label) }),
  departments: (d) => ({ id: d.id, code: str(d.code), name: str(d.name), color: d.color, order: d.order }),
  noteLayers: (l) => ({ id: l.id, name: str(l.name), color: l.color, order: l.order }),
  moments: (m) => ({ id: m.id, anchor: anchor(m.anchor), scriptText: str(m.scriptText) }),
  cues: (c) => ({
    id: c.id,
    moment: c.moment,
    department: c.department,
    number: str(c.number),
    label: str(c.label),
    notes: str(c.notes),
  }),
  standbys: (s) => ({ id: s.id, anchor: anchor(s.anchor), through: s.through }),
  notes: (n) => ({ id: n.id, layer: n.layer, anchor: anchor(n.anchor), style: n.style, ...(n.placement === undefined ? {} : { placement: n.placement }), text: str(n.text) }),
};

/** One record as its single serialized line (without indentation or trailing comma). */
export function recordLine(type: RecordType, record: AnyRecord): string {
  // `type` names the record's kind; TypeScript can't correlate the two unions, so widen the canonicalizer.
  const canon = canonical[type] as (r: AnyRecord) => AnyRecord;
  return JSON.stringify(canon(record));
}

const byId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

function array<K extends RecordType>(name: K, items: Records[K][]): string {
  if (!items.length) return `  "${name}": []`;
  return `  "${name}": [\n${items.map((r) => `    ${recordLine(name, r)}`).join(",\n")}\n  ]`;
}

export function serialize(doc: Doc): string {
  const parts = [
    `  "format": "q2q"`,
    `  "formatVersion": ${doc.formatVersion}`,
    `  "project": ${recordLine("project", doc.project)}`,
    array("pages", [...doc.pages].sort((a, b) => a.page - b.page)),
    array("departments", [...doc.departments].sort(byId)),
    array("noteLayers", [...doc.noteLayers].sort(byId)),
    array("moments", [...doc.moments].sort(byId)),
    array("cues", [...doc.cues].sort(byId)),
    array("standbys", [...doc.standbys].sort(byId)),
    array("notes", [...doc.notes].sort(byId)),
  ];
  return `{\n${parts.join(",\n")}\n}\n`;
}
